import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatDate } from "utils/formatDate";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import { FormDialog, selectOptionSchema } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps,
  SelectOption
} from "components/common/FormDialog/types";
import { Head } from "components/common/Head";
import { Menu } from "components/common/Menu";
import { Table as CommonTable } from "components/common/Table";
import {
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { clustersActions } from "modules/clusters/slices";
import {
  areClusterSettingsLoadingSelector,
  areNodegroupsLoadingSelector,
  clusterSelector,
  clusterSettingsSelector,
  clusterTemplatesSelector,
  isAnyClusterOperationInProgressSelector,
  isAnyNodegroupOperationInProgressSelector,
  isClusterCARotatingSelector,
  isClusterDeletingSelector,
  isClusterSettingsUpdatingSelector,
  isClusterUpgradingSelector,
  isNodegroupCreatingSelector,
  isNodegroupDeletingSelector,
  isNodegroupDetailsLoadingSelector,
  isNodegroupUpdatingSelector,
  nodegroupsSelector,
  selectedNodegroupDetailsSelector,
  TableNodegroup,
  tableNodegroupsSelector
} from "modules/clusters/selectors";
import { CreateNodegroupParams, Nodegroup } from "modules/clusters/types";
import { enterprisesActions } from "modules/enterprises/slices";
import { organizationSelector } from "modules/enterprises/selectors";
import { instancesActions } from "modules/instances/slices";
import {
  areFlavorsLoadingSelector,
  flavorsSelector
} from "modules/instances/selectors";
import { FLAVOR_TYPE } from "modules/instances/types";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { pollingActions } from "modules/polling/slices";
import { createPolling } from "modules/polling/utils";
import { projectsActions } from "modules/projects/slices";
import { projectSelector } from "modules/projects/selectors";
import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { downloadFile } from "utils/downloadFile";
import { filterVersions } from "utils/filterVersions";
import { generateSearchString } from "utils/generateSearchString";
import { number, string } from "yup";
import { API_PATH } from "../../axios";
import { DATE_FORMATS, ROUTES } from "../../constants";
import * as s from "./styles";
import { DIALOG_TYPES, TABS } from "./types";

// --- START: Define Form Data Interfaces ---
interface AddNodegroupFormData {
  name: string;
  flavor_id: SelectOption;
  cluster_template?: SelectOption;
  is_autoscaling_enabled: boolean;
  node_count?: number;
  min_node_count?: number;
  max_node_count?: number;
}

interface AutoscaleNodegroupFormData {
  is_autoscaling_enabled: boolean;
  min_node_count?: number;
  max_node_count?: number;
}
// --- END: Define Form Data Interfaces ---

const POLL_ID_PREFIX = "CLUSTER";

const POLL_IDS = {
  cluster: "CLUSTER",
  clusterTemplates: "CLUSTER_TEMPLATES",
  nodegroups: "NODEGROUPS",
  clusterSettings: "CLUSTER_SETTINGS"
};

const TAB_TITLES = {
  [TABS.NODE_POOLS]: "Node Pools",
  [TABS.SETTINGS]: "Settings",
  [TABS.LABELS]: "Labels"
};

type LabelRow = { id: string; key: string; value: string };

const labelTableColumns: TableColumn<LabelRow>[] = [
  { key: "key", label: "Key" },
  { key: "value", label: "Value" }
];

export const Cluster: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    organizationId: string;
    regionId: string;
    projectId: string;
    clusterId: string;
  }>();
  const history = useNavigate();
  const organization = useSelector(organizationSelector);
  const project = useSelector(projectSelector);
  const cluster = useSelector(clusterSelector);
  const clusterTemplates = useSelector(clusterTemplatesSelector);
  const isClusterDeleting = useSelector(isClusterDeletingSelector);
  const isClusterUpgrading = useSelector(isClusterUpgradingSelector);
  const isClusterCARotating = useSelector(isClusterCARotatingSelector);
  const isClusterOperationInProgress = useSelector(
    isAnyClusterOperationInProgressSelector
  );
  const flavors = useSelector(flavorsSelector);
  const nodegroups = useSelector(nodegroupsSelector);
  const processedNodegroups = useSelector(tableNodegroupsSelector);
  const areNodegroupsLoading = useSelector(areNodegroupsLoadingSelector);
  const isNodegroupOperationInProgress = useSelector(
    isAnyNodegroupOperationInProgressSelector
  );
  const isNodegroupCreating = useSelector(isNodegroupCreatingSelector);
  const isNodegroupUpdating = useSelector(isNodegroupUpdatingSelector);
  const isNodegroupDeleting = useSelector(isNodegroupDeletingSelector);
  const isAnyOperationInProgress =
    isClusterOperationInProgress || isNodegroupOperationInProgress;
  const previousIsAnyOperationInProgress = usePrevious(
    isAnyOperationInProgress
  );
  const previousIsClusterDeleting = usePrevious(isClusterDeleting);
  const previousIsNodegroupCreating = usePrevious(isNodegroupCreating);
  const previousIsNodegroupUpdating = usePrevious(isNodegroupUpdating);
  const previousIsNodegroupDeleting = usePrevious(isNodegroupDeleting);
  const selectedNodegroupDetails = useSelector(
    selectedNodegroupDetailsSelector
  );
  const isNodegroupDetailsLoading = useSelector(
    isNodegroupDetailsLoadingSelector
  );
  const areFlavorsLoading = useSelector(areFlavorsLoadingSelector);
  const clusterSettings = useSelector(clusterSettingsSelector);
  const areClusterSettingsLoading = useSelector(
    areClusterSettingsLoadingSelector
  );
  const isClusterSettingsUpdating = useSelector(
    isClusterSettingsUpdatingSelector
  );
  const [settingsValues, setSettingsValues] = useState<Record<string, string>>(
    {}
  );
  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.UPGRADE, isOpened: false });
  const activeTabIndexFromParam = Object.keys(TAB_TITLES).find(
    (key) =>
      TAB_TITLES[Number(key)] &&
      String(TAB_TITLES[Number(key)]).toLowerCase() ===
        new URLSearchParams(location.search).get("tab")
  );
  const [activeTabIndex, setActiveTabIndex] = useState<TABS>(
    Number(activeTabIndexFromParam || TABS.NODE_POOLS)
  );
  const previousActiveTabIndex = usePrevious(activeTabIndex);
  const [isActionsMenuOpened, setIsActionsMenuOpened] =
    useState<boolean>(false);
  const actionsMenuButtonRef = useRef<HTMLButtonElement | null>(null);

  const [nodegroupToDelete, setNodegroupToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedNodegroupForEdit, setSelectedNodegroupForEdit] =
    useState<Nodegroup | null>(null);
  const [targetNodegroupIdForEdit, setTargetNodegroupIdForEdit] = useState<
    string | null
  >(null);
  const [intendedDialogType, setIntendedDialogType] =
    useState<DIALOG_TYPES | null>(null);

  const handleChangeTab = useCallback(
    (e: unknown, value: number) => {
      setActiveTabIndex(value);
      const tabName = TAB_TITLES[value];
      // Only include tab in search string if it's a valid string
      const searchParams: { tab?: string } = {};
      if (typeof tabName === "string") {
        searchParams.tab = tabName.toLowerCase();
      }
      const newSearch = generateSearchString(searchParams);
      history({ search: newSearch }, { replace: true });
    },
    [history]
  );

  const handleCloseDialog = useCallback(() => {
    setDialog({ ...dialog, isOpened: false });
    setSelectedNodegroupForEdit(null);
    setNodegroupToDelete(null);
    setTargetNodegroupIdForEdit(null);
    setIntendedDialogType(null);
  }, [dialog]);

  const handleActionsMenuButtonClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      actionsMenuButtonRef.current = e.currentTarget;
      setIsActionsMenuOpened(!isActionsMenuOpened);
    },
    [isActionsMenuOpened]
  );

  const handleActionsMenuClose = useCallback(() => {
    setIsActionsMenuOpened(false);
  }, []);

  const handleActionsMenuItemClick = useCallback(
    (callback: () => void) => () => {
      setIsActionsMenuOpened(false);
      callback();
    },
    []
  );

  const handleUpgradeClusterMenuItemClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.UPGRADE,
      isOpened: true
    });
  }, []);

  const handleDownloadKubeconfigFileClick = useCallback(() => {
    downloadFile(
      `${API_PATH}gotham-${matchParams.regionId}-kubernetes/method/${matchParams.projectId}/clusters/${matchParams.clusterId}/kubeconfig`
    );
    dispatch(
      notificationsActions.showNotification({
        title: "Kubeconfig file is generating. Download will start soon.",
        type: NOTIFICATION_TYPES.INFO
      })
    );
  }, [
    matchParams.regionId,
    matchParams.projectId,
    matchParams.clusterId,
    dispatch
  ]);

  const handleDeleteClusterButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.DELETE,
      isOpened: true
    });
  }, []);

  const handleRotateCAMenuItemClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.ROTATE_CA,
      isOpened: true
    });
  }, []);

  const handleConfirmRotateCA = useCallback(() => {
    dispatch(
      clustersActions.rotateClusterCARequest({
        regionId: matchParams.regionId!,
        projectId: matchParams.projectId!,
        id: matchParams.clusterId!
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    matchParams.regionId,
    matchParams.projectId,
    matchParams.clusterId,
    handleCloseDialog
  ]);

  const handleAddNodegroupClick = useCallback(() => {
    setDialog({ type: DIALOG_TYPES.NODEGROUP_ADD, isOpened: true });
  }, []);

  const triggerNodegroupFetchAndOpenDialog = useCallback(
    (nodegroupId: string, dialogType: DIALOG_TYPES) => {
      if (
        matchParams.regionId &&
        matchParams.projectId &&
        matchParams.clusterId
      ) {
        setTargetNodegroupIdForEdit(nodegroupId);
        setIntendedDialogType(dialogType);
        dispatch(clustersActions.clearSelectedNodegroup());
        dispatch(
          clustersActions.getNodegroupRequest({
            regionId: matchParams.regionId,
            projectId: matchParams.projectId,
            clusterId: matchParams.clusterId,
            nodegroupId: nodegroupId
          })
        );
        if (dialogType === DIALOG_TYPES.NODEGROUP_UPGRADE) {
          if (!clusterTemplates || clusterTemplates.length === 0) {
            dispatch(
              clustersActions.getClusterTemplatesRequest({
                regionId: matchParams.regionId,
                projectId: matchParams.projectId
              })
            );
          }
        }
      }
    },
    [dispatch, matchParams, clusterTemplates]
  );

  const handleResizeNodegroupClick = useCallback(
    (id: string) => {
      triggerNodegroupFetchAndOpenDialog(id, DIALOG_TYPES.NODEGROUP_RESIZE);
    },
    [triggerNodegroupFetchAndOpenDialog]
  );

  const handleAutoscaleNodegroupClick = useCallback(
    (id: string) => {
      triggerNodegroupFetchAndOpenDialog(id, DIALOG_TYPES.NODEGROUP_AUTOSCALE);
    },
    [triggerNodegroupFetchAndOpenDialog]
  );

  const handleUpgradeNodegroupClick = useCallback(
    (id: string) => {
      triggerNodegroupFetchAndOpenDialog(id, DIALOG_TYPES.NODEGROUP_UPGRADE);
    },
    [triggerNodegroupFetchAndOpenDialog]
  );

  const handleDeleteNodegroupClick = useCallback((nodegroup: Nodegroup) => {
    setNodegroupToDelete({ id: nodegroup.id, name: nodegroup.name });
    setDialog({ type: DIALOG_TYPES.NODEGROUP_DELETE, isOpened: true });
  }, []);

  const handleConfirmUpgradeCluster = useCallback(
    (data: { cluster_template: SelectOption }) => {
      dispatch(
        clustersActions.upgradeClusterRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          id: matchParams.clusterId!,
          data: {
            cluster_template: data.cluster_template.value
          }
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      matchParams.regionId,
      matchParams.projectId,
      matchParams.clusterId,
      handleCloseDialog
    ]
  );

  const handleConfirmDeleteCluster = useCallback(() => {
    dispatch(
      clustersActions.deleteClusterRequest({
        regionId: matchParams.regionId!,
        projectId: matchParams.projectId!,
        id: matchParams.clusterId!
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    matchParams.projectId,
    matchParams.clusterId,
    matchParams.regionId,
    handleCloseDialog
  ]);

  const handleConfirmCreateNodegroup = useCallback(
    (data: AddNodegroupFormData) => {
      const labels: Record<string, string> = {};
      let selectedImageId: string | undefined;

      if (data.cluster_template?.value) {
        const selectedTemplate = clusterTemplates?.find(
          (template) => template.uuid === data.cluster_template?.value
        );

        if (selectedTemplate) {
          labels.cluster_template_id = data.cluster_template.value;

          if (selectedTemplate.image_id) {
            selectedImageId = selectedTemplate.image_id;
          }
        }
      }

      const payload: CreateNodegroupParams = {
        regionId: matchParams.regionId!,
        projectId: matchParams.projectId!,
        clusterId: matchParams.clusterId!,
        data: {
          name: data.name,
          flavor_id: data.flavor_id.value,
          node_count: data.is_autoscaling_enabled
            ? Number(data.min_node_count) || 0
            : Number(data.node_count) || 0,
          is_autoscaling_enabled: data.is_autoscaling_enabled,
          ...(data.is_autoscaling_enabled && {
            min_node_count: Number(data.min_node_count) || 0,
            max_node_count: Number(data.max_node_count) || 0
          }),
          ...(selectedImageId && { image_id: selectedImageId }),
          ...(Object.keys(labels).length > 0 && { labels })
        }
      };
      dispatch(clustersActions.createNodegroupRequest(payload));
      handleCloseDialog();
    },
    [
      matchParams.regionId,
      matchParams.projectId,
      matchParams.clusterId,
      dispatch,
      handleCloseDialog,
      clusterTemplates
    ]
  );

  const handleConfirmResizeNodegroup = useCallback(
    (data: { node_count: number }) => {
      if (!selectedNodegroupForEdit) return;
      dispatch(
        clustersActions.resizeClusterRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          id: matchParams.clusterId!,
          data: {
            node_count: Number(data.node_count) || 0,
            nodegroup: selectedNodegroupForEdit.id
          }
        })
      );
      handleCloseDialog();
    },
    [dispatch, matchParams, selectedNodegroupForEdit, handleCloseDialog]
  );

  const handleConfirmAutoscaleNodegroup = useCallback(
    (data: AutoscaleNodegroupFormData) => {
      if (!selectedNodegroupForEdit) return;
      const payloadData: {
        min_node_count?: number | undefined;
        max_node_count?: number | undefined;
      } = {};

      if (data.is_autoscaling_enabled) {
        payloadData.min_node_count = Number(data.min_node_count) || 0;
        payloadData.max_node_count = Number(data.max_node_count) || 0;
      } else {
        payloadData.min_node_count = 0;
        payloadData.max_node_count = undefined;
      }

      dispatch(
        clustersActions.updateNodegroupRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          clusterId: matchParams.clusterId!,
          nodegroupId: selectedNodegroupForEdit.id,
          data: payloadData
        })
      );
      handleCloseDialog();
    },
    [dispatch, matchParams, selectedNodegroupForEdit, handleCloseDialog]
  );

  const handleConfirmUpgradeNodegroup = useCallback(
    (data: { cluster_template: SelectOption }) => {
      if (!selectedNodegroupForEdit) return;
      dispatch(
        clustersActions.upgradeClusterRequest({
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!,
          id: matchParams.clusterId!,
          data: {
            cluster_template: data.cluster_template.value,
            nodegroup: selectedNodegroupForEdit.id
          }
        })
      );
      handleCloseDialog();
    },
    [dispatch, matchParams, selectedNodegroupForEdit, handleCloseDialog]
  );

  const handleConfirmDeleteNodegroup = useCallback(() => {
    if (!nodegroupToDelete) return;
    dispatch(
      clustersActions.deleteNodegroupRequest({
        regionId: matchParams.regionId!,
        projectId: matchParams.projectId!,
        clusterId: matchParams.clusterId!,
        nodegroupId: nodegroupToDelete.id
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    matchParams.regionId,
    matchParams.projectId,
    matchParams.clusterId,
    nodegroupToDelete,
    handleCloseDialog
  ]);

  const handleDeleteActionClick = useCallback(
    (id: string) => {
      const nodegroup = nodegroups?.find((ng) => ng.id === id);
      if (nodegroup) {
        handleDeleteNodegroupClick(nodegroup);
      }
    },
    [nodegroups, handleDeleteNodegroupClick]
  );

  useMount(() => {
    if (
      !matchParams.regionId ||
      !matchParams.projectId ||
      !matchParams.clusterId ||
      !matchParams.organizationId
    )
      return;

    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.cluster}`,
        actionCreator: clustersActions.getClusterRequest,
        params: {
          regionId: matchParams.regionId,
          projectId: matchParams.projectId,
          id: matchParams.clusterId
        }
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.nodegroups}`,
        actionCreator: clustersActions.listNodegroupsRequest,
        params: {
          regionId: matchParams.regionId,
          projectId: matchParams.projectId,
          clusterId: matchParams.clusterId
        }
      })
    );

    dispatch(
      instancesActions.getFlavorsRequest({
        regionId: matchParams.regionId,
        projectId: matchParams.projectId,
        type: FLAVOR_TYPE.CLUSTER
      })
    );
    dispatch(
      clustersActions.getClusterTemplatesRequest({
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    );
    dispatch(
      enterprisesActions.getOrganizationRequest({
        id: matchParams.organizationId
      })
    );
    dispatch(
      projectsActions.getProjectRequest({
        regionId: matchParams.regionId,
        id: matchParams.projectId
      })
    );
  });

  useUnmount(() => {
    const activePolls = [
      POLL_IDS.cluster,
      POLL_IDS.clusterTemplates,
      POLL_IDS.nodegroups,
      POLL_IDS.clusterSettings
    ];
    activePolls.forEach((idSuffix) => {
      dispatch(
        pollingActions.stopPolling({ id: `${POLL_ID_PREFIX}/${idSuffix}` })
      );
    });
    dispatch(enterprisesActions.clear());
    dispatch(projectsActions.clear());
    dispatch(clustersActions.clear());
    dispatch(instancesActions.clear());
  });

  // Start/stop settings polling based on active tab
  useEffect(() => {
    if (previousActiveTabIndex === activeTabIndex) return;
    if (
      !matchParams.regionId ||
      !matchParams.projectId ||
      !matchParams.clusterId
    )
      return;

    if (activeTabIndex === TABS.SETTINGS) {
      dispatch(
        createPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.clusterSettings}`,
          actionCreator: clustersActions.getClusterSettingsRequest,
          params: {
            regionId: matchParams.regionId,
            projectId: matchParams.projectId,
            clusterId: matchParams.clusterId
          }
        })
      );
    } else if (previousActiveTabIndex === TABS.SETTINGS) {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.clusterSettings}`
        })
      );
    }
  }, [
    activeTabIndex,
    previousActiveTabIndex,
    dispatch,
    matchParams.regionId,
    matchParams.projectId,
    matchParams.clusterId
  ]);

  useEffect(() => {
    if (previousIsAnyOperationInProgress && !isAnyOperationInProgress) {
      // If cluster deletion just completed, refresh to check if cluster still exists
      if (previousIsClusterDeleting && !isClusterDeleting) {
        dispatch(
          clustersActions.getClusterRequest({
            regionId: matchParams.regionId!,
            projectId: matchParams.projectId!,
            id: matchParams.clusterId!
          })
        );
      } else if (
        !isNodegroupOperationInProgress &&
        isClusterOperationInProgress
      ) {
        // Refresh cluster data when other operations complete
        dispatch(
          clustersActions.getClusterRequest({
            regionId: matchParams.regionId!,
            projectId: matchParams.projectId!,
            id: matchParams.clusterId!
          })
        );
      }
    }
  }, [
    cluster,
    history,
    previousIsAnyOperationInProgress,
    isAnyOperationInProgress,
    isClusterOperationInProgress,
    isNodegroupOperationInProgress,
    isClusterDeleting,
    previousIsClusterDeleting,
    dispatch,
    matchParams
  ]);

  // Refresh nodegroups when nodegroup operations complete
  useEffect(() => {
    const shouldRefreshNodegroups =
      (previousIsNodegroupCreating && !isNodegroupCreating) ||
      (previousIsNodegroupUpdating && !isNodegroupUpdating) ||
      (previousIsNodegroupDeleting && !isNodegroupDeleting);

    if (
      shouldRefreshNodegroups &&
      matchParams.clusterId &&
      matchParams.regionId &&
      matchParams.projectId
    ) {
      dispatch(
        clustersActions.listNodegroupsRequest({
          regionId: matchParams.regionId,
          projectId: matchParams.projectId,
          clusterId: matchParams.clusterId
        })
      );
    }
  }, [
    previousIsNodegroupCreating,
    isNodegroupCreating,
    previousIsNodegroupUpdating,
    isNodegroupUpdating,
    previousIsNodegroupDeleting,
    isNodegroupDeleting,
    matchParams.clusterId,
    matchParams.regionId,
    matchParams.projectId,
    dispatch
  ]);

  useEffect(() => {
    if (
      targetNodegroupIdForEdit &&
      !isNodegroupDetailsLoading &&
      selectedNodegroupDetails &&
      selectedNodegroupDetails.id === targetNodegroupIdForEdit &&
      intendedDialogType !== null
    ) {
      setSelectedNodegroupForEdit(selectedNodegroupDetails);
      setDialog({ type: intendedDialogType, isOpened: true });
      setTargetNodegroupIdForEdit(null);
      setIntendedDialogType(null);
    } else if (
      targetNodegroupIdForEdit &&
      !isNodegroupDetailsLoading &&
      (!selectedNodegroupDetails ||
        selectedNodegroupDetails.id !== targetNodegroupIdForEdit)
    ) {
      setTargetNodegroupIdForEdit(null);
      setIntendedDialogType(null);
      dispatch(
        notificationsActions.showNotification({
          title: "Failed to load nodegroup details for editing.",
          type: NOTIFICATION_TYPES.ERROR
        })
      );
    }
  }, [
    targetNodegroupIdForEdit,
    isNodegroupDetailsLoading,
    selectedNodegroupDetails,
    intendedDialogType,
    dispatch
  ]);

  const clusterActions = [
    {
      label: "Download kubeconfig file",
      handler: handleDownloadKubeconfigFileClick,
      disabled: isAnyOperationInProgress
    },
    {
      label: "Upgrade",
      handler: handleUpgradeClusterMenuItemClick,
      disabled: isAnyOperationInProgress
    },
    {
      label: "Rotate CA",
      handler: handleRotateCAMenuItemClick,
      disabled: isAnyOperationInProgress
    }
  ];

  const breadcrumbs: Breadcrumb[] = [
    { text: "Organizations", url: ROUTES.ORGANIZATIONS },
    {
      text: organization?.name || "",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: "Clusters",
      url: generatePath(ROUTES.CLUSTERS, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    },
    {
      text: cluster?.name || "",
      url: generatePath(ROUTES.CLUSTER, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId,
        clusterId: matchParams.clusterId
      })
    }
  ].filter((b) => b.text && b.url);

  const columns: TableColumn<TableNodegroup>[] = [
    { key: "name", label: "Name" },
    {
      key: "status",
      label: "Status"
    },
    {
      key: "node_count",
      label: "Nodes",
      render: (ng) => {
        const isAutoscalingEnabled =
          ng.min_node_count != null && ng.max_node_count != null;
        return isAutoscalingEnabled
          ? `${ng.node_count ?? 0} (${ng.min_node_count ?? 0} - ${ng.max_node_count ?? 0})`
          : (ng.node_count ?? 0);
      }
    },
    {
      key: "flavorName",
      label: "Flavor"
    },
    {
      key: "image_id",
      label: "Image",
      render: (ng) => ng.image_id || "Default"
    }
  ];

  const nodegroupTableActions: TableRowActionsMenuItem<TableNodegroup>[] =
    useMemo(
      () => [
        {
          label: "Resize",
          handler: handleResizeNodegroupClick,
          isDisabled: (ng) =>
            isAnyOperationInProgress || ng.status.includes("PROGRESS")
        },
        {
          label: "Configure Autoscaling",
          handler: handleAutoscaleNodegroupClick,
          isDisabled: (ng) =>
            isAnyOperationInProgress ||
            ng.status.includes("PROGRESS") ||
            ng.role === "master"
        },
        {
          label: "Upgrade",
          handler: handleUpgradeNodegroupClick,
          isDisabled: (ng) =>
            isAnyOperationInProgress ||
            ng.status.includes("PROGRESS") ||
            ng.role === "master" ||
            ng.is_default
        },
        {
          label: "Delete",
          handler: handleDeleteActionClick,
          isDisabled: (ng) => isAnyOperationInProgress || !!ng.is_default
        }
      ],
      [
        handleResizeNodegroupClick,
        handleAutoscaleNodegroupClick,
        handleUpgradeNodegroupClick,
        handleDeleteActionClick,
        isAnyOperationInProgress
      ]
    );

  const nodePoolToolbar = useMemo(
    () => (
      <Tooltip
        title={
          !cluster?.api_address
            ? "Cannot add node pools until cluster API is available"
            : ""
        }
      >
        <span>
          <Button
            variant="contained"
            onClick={handleAddNodegroupClick}
            disabled={isAnyOperationInProgress || !cluster?.api_address}
            startIcon={<AddIcon />}
          >
            Add Node Pool
          </Button>
        </span>
      </Tooltip>
    ),
    [handleAddNodegroupClick, isAnyOperationInProgress, cluster?.api_address]
  );

  // Sync cluster settings into local toggle state only when no pending changes
  useEffect(() => {
    if (!clusterSettings) return;
    const hasLocalChanges = clusterSettings.some((s) => {
      const original =
        s.value_type === "bool" ? s.value.toLowerCase() : s.value;
      return (
        settingsValues[s.key] !== undefined &&
        settingsValues[s.key] !== original
      );
    });
    if (hasLocalChanges) return;
    const values: Record<string, string> = {};
    clusterSettings.forEach((s) => {
      values[s.key] = s.value_type === "bool" ? s.value.toLowerCase() : s.value;
    });
    setSettingsValues(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterSettings]);

  const groupedSettings = useMemo(() => {
    if (!clusterSettings) return [];
    const groupMap = new Map<string, typeof clusterSettings>();
    clusterSettings.forEach((s) => {
      const group = groupMap.get(s.group);
      if (group) {
        group.push(s);
      } else {
        groupMap.set(s.group, [s]);
      }
    });
    return Array.from(groupMap.entries()).map(([group, settings]) => ({
      group,
      settings
    }));
  }, [clusterSettings]);

  const hasSettingsChanges = useMemo(() => {
    if (!clusterSettings) return false;
    return clusterSettings.some((s) => {
      const original =
        s.value_type === "bool" ? s.value.toLowerCase() : s.value;
      return settingsValues[s.key] !== original;
    });
  }, [clusterSettings, settingsValues]);

  const changedSettings = useMemo(() => {
    if (!clusterSettings) return {};
    const changed: Record<string, string> = {};
    clusterSettings.forEach((s) => {
      const original =
        s.value_type === "bool" ? s.value.toLowerCase() : s.value;
      if (settingsValues[s.key] !== original) {
        changed[s.key] = settingsValues[s.key];
      }
    });
    return changed;
  }, [clusterSettings, settingsValues]);

  const handleSettingToggle = useCallback((key: string) => {
    setSettingsValues((prev) => ({
      ...prev,
      [key]: prev[key] === "true" ? "false" : "true"
    }));
  }, []);

  const handleSaveSettings = useCallback(() => {
    if (
      !matchParams.regionId ||
      !matchParams.projectId ||
      !matchParams.clusterId
    )
      return;
    dispatch(
      clustersActions.updateClusterSettingsRequest({
        regionId: matchParams.regionId,
        projectId: matchParams.projectId,
        clusterId: matchParams.clusterId,
        data: { labels: changedSettings }
      })
    );
  }, [
    dispatch,
    matchParams.regionId,
    matchParams.projectId,
    matchParams.clusterId,
    changedSettings
  ]);

  const labelRows: LabelRow[] = cluster?.labels
    ? Object.entries(cluster.labels).map(([key, value]) => ({
        id: key,
        key: key,
        value: value
      }))
    : [];

  const dialogProps: {
    [key in DIALOG_TYPES]?: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.UPGRADE]: {
      onConfirm: handleConfirmUpgradeCluster,
      title: "Upgrade Cluster",
      confirmButtonLabel: "Upgrade",
      isLocked: isClusterUpgrading,
      fields: [
        // Show warning if cluster has less than 2 master nodes (not HA)
        ...(cluster && cluster.master_count < 2
          ? [
              {
                name: "ha_warning",
                type: FIELD_TYPES.LABEL,
                label: "⚠️ High Availability Warning"
              },
              {
                name: "ha_warning_text",
                type: FIELD_TYPES.NOTES,
                label:
                  "\nThis cluster has only 1 master node and is not running in High Availability (HA) mode.\n\nDuring the upgrade process, if something goes wrong, access to the cluster may be lost.\n\nConsider scaling to 2+ master nodes before upgrading for better reliability."
              }
            ]
          : []),
        {
          name: "cluster_template",
          type: FIELD_TYPES.SELECT,
          label: "Cluster template",
          helperText:
            "✍ You can choose the current version to re-apply the configuration.",
          options: () => {
            const currentTemplateName = cluster?.labels.kube_tag;
            const currentClusterTemplate = clusterTemplates?.find(
              (template) => template.uuid === cluster?.cluster_template_id
            );
            const clusterImagePrefix = currentClusterTemplate?.image_id
              ? currentClusterTemplate.image_id.substring(0, 6).toLowerCase()
              : null;

            if (
              !currentTemplateName ||
              !clusterTemplates ||
              !clusterImagePrefix
            ) {
              return null;
            }

            const imageCompatibleTemplates = clusterTemplates.filter(
              (template) => {
                const templateImagePrefix = template.image_id
                  ? template.image_id.substring(0, 6).toLowerCase()
                  : null;
                return templateImagePrefix === clusterImagePrefix;
              }
            );

            return filterVersions(
              currentTemplateName,
              imageCompatibleTemplates,
              (obj) => obj.name
            ).map(({ name, uuid }) => ({
              label: name === currentTemplateName ? `${name} (current)` : name,
              value: uuid
            }));
          },
          rules: selectOptionSchema.required("Cluster template is required")
        }
      ]
    },
    [DIALOG_TYPES.DELETE]: {
      onConfirm: handleConfirmDeleteCluster,
      title: `Are you sure you want to delete "${cluster?.name ?? "selected"}" cluster?`,
      confirmButtonLabel: "Delete",
      isLocked: isClusterDeleting,
      fields: [
        {
          name: "confirmationName",
          type: FIELD_TYPES.TEXT,
          label: `Type the cluster name "${cluster?.name}" to confirm`,
          rules: string()
            .required()
            .test(
              "validateConfirmationName",
              "Cluster name does not match",
              (value) => value === cluster?.name
            )
        }
      ]
    },
    [DIALOG_TYPES.ROTATE_CA]: {
      onConfirm: handleConfirmRotateCA,
      title: `Rotate CA certificate for "${cluster?.name ?? "selected"}" cluster?`,
      confirmButtonLabel: "Rotate CA",
      isLocked: isClusterCARotating,
      fields: [
        {
          name: "confirmationName",
          type: FIELD_TYPES.TEXT,
          label: `Type the cluster name "${cluster?.name}" to confirm`,
          rules: string()
            .required()
            .test(
              "validateConfirmationName",
              "Cluster name does not match",
              (value) => value === cluster?.name
            )
        },
        {
          name: "rotateCAInfo",
          type: FIELD_TYPES.LABEL,
          label: `\n\nℹ️ ROTATE CA CERTIFICATE: This action will regenerate the cluster's Certificate Authority.\n\nThis operation will:\n\n- Regenerate the cluster CA certificate\n- Temporarily disrupt cluster connectivity\n- Invalidate existing kubeconfig files\n- All pods may be restarted during the process\n\nBefore proceeding:\n✓ Download a backup of your current kubeconfig\n✓ Ensure workloads can tolerate temporary disruption\n✓ Plan to re-download kubeconfig after rotation completes`
        }
      ]
    },
    [DIALOG_TYPES.NODEGROUP_ADD]: {
      onConfirm: handleConfirmCreateNodegroup,
      title: "Add Node Pool",
      confirmButtonLabel: "Add",
      isLocked: isNodegroupCreating,
      fields: [
        {
          name: "name",
          label: "Node Pool Name",
          type: FIELD_TYPES.TEXT,
          rules: string().required()
        },
        {
          name: "flavor_id",
          label: "Flavor",
          type: FIELD_TYPES.SELECT,
          options:
            flavors?.map((f) => ({
              label: `${f.name} (${f.vcpus} vCPU, ${f.ram / 1024} GiB RAM)`,
              value: f.id
            })) || [],
          rules: selectOptionSchema.required("Flavor is required")
        },
        {
          name: "cluster_template",
          label: "Cluster Template",
          type: FIELD_TYPES.SELECT,
          helperText:
            "Inherit labels from a template. Only versions equal to or higher than current master version are shown.",
          options: () => {
            if (!clusterTemplates || !cluster?.labels?.kube_tag) {
              return [];
            }

            // Filter by version (current version or higher)
            const versionFilteredTemplates = filterVersions(
              cluster.labels.kube_tag,
              clusterTemplates,
              (template) => template.name
            );

            return versionFilteredTemplates.map((ct) => ({
              label:
                ct.name === cluster.labels.kube_tag
                  ? `${ct.name} (current)`
                  : ct.name,
              value: ct.uuid
            }));
          },
          rules: selectOptionSchema.required("Cluster template is required")
        },
        {
          name: "is_autoscaling_enabled",
          label: "Enable Autoscaling",
          type: FIELD_TYPES.TOGGLE,
          defaultValue: false
        },
        {
          name: "node_count",
          label: "Node Count",
          type: FIELD_TYPES.NUMBER,
          defaultValue: 1,
          rules: number()
            .integer()
            .min(0)
            .when("is_autoscaling_enabled", {
              is: false,
              then: (s) => s.required()
            }),
          isHidden: (data) =>
            !!(data as unknown as AddNodegroupFormData).is_autoscaling_enabled
        },
        {
          name: "min_node_count",
          label: "Min Nodes",
          type: FIELD_TYPES.NUMBER,
          defaultValue: 1,
          rules: number()
            .integer()
            .min(0)
            .when("is_autoscaling_enabled", {
              is: true,
              then: (s) => s.required()
            }),
          isHidden: (data) =>
            !(data as unknown as AddNodegroupFormData).is_autoscaling_enabled
        },
        {
          name: "max_node_count",
          label: "Max Nodes",
          type: FIELD_TYPES.NUMBER,
          defaultValue: 1,
          rules: number()
            .integer()
            .min(0)
            .when("is_autoscaling_enabled", {
              is: true,
              then: (s) =>
                s
                  .required()
                  .test("min-max", "Must be >= min nodes", function (v) {
                    const minCount = (
                      this.parent as unknown as AddNodegroupFormData
                    ).min_node_count;
                    return (
                      v === undefined || minCount === undefined || v >= minCount
                    );
                  })
            }),
          isHidden: (data) =>
            !(data as unknown as AddNodegroupFormData).is_autoscaling_enabled
        }
      ]
    },
    [DIALOG_TYPES.NODEGROUP_DELETE]: {
      onConfirm: handleConfirmDeleteNodegroup,
      title: `Are you sure you want to delete "${nodegroupToDelete?.name ?? "selected"}" node pool?`,
      confirmButtonLabel: "Delete",
      isLocked: isNodegroupDeleting,
      fields: [
        {
          name: "confirmationName",
          type: FIELD_TYPES.TEXT,
          label: "Type the node pool name to confirm deletion",
          rules: string()
            .required()
            .test({
              name: "validateConfirmationName",
              message: "Node pool name does not match",
              test: function (value) {
                return value === nodegroupToDelete?.name;
              }
            })
        },
        {
          name: "deletionInfo",
          type: FIELD_TYPES.LABEL,
          label: `\n\nℹ️ DELETE NODE POOL: This action is PERMANENT and cannot be undone.\n\nDeleting this pool will:\n\n- Immediately terminate all nodes in the pool\n- Stop all running pods on these nodes\n- Permanently delete any local/ephemeral storage\n- Potentially cause service disruption\n\nBefore proceeding:\n✓ Ensure critical workloads can reschedule to other nodes\n✓ Verify sufficient capacity exists in remaining pools\n✓ Consider draining nodes first to gracefully migrate workloads`
        }
      ]
    },
    [DIALOG_TYPES.NODEGROUP_RESIZE]: {
      onConfirm: handleConfirmResizeNodegroup,
      title: `Resize Node Pool "${selectedNodegroupForEdit?.name ?? ""}"`,
      confirmButtonLabel: "Resize",
      isLoading: isNodegroupDetailsLoading,
      isLocked: isAnyOperationInProgress,
      fields: [
        {
          name: "node_count",
          label: "Node Count",
          type: FIELD_TYPES.NUMBER,
          defaultValue:
            selectedNodegroupForEdit?.node_count != null
              ? selectedNodegroupForEdit.node_count.toString()
              : "0",
          min: 0,
          rules: number().integer().min(0).required(),
          helperText:
            selectedNodegroupForEdit?.max_node_count != null
              ? "Warning: Autoscaling is enabled. This count may be overridden by the autoscaler based on its configuration (min/max nodes)."
              : ""
        }
      ]
    },
    [DIALOG_TYPES.NODEGROUP_AUTOSCALE]: {
      onConfirm: handleConfirmAutoscaleNodegroup,
      title: `Configure Autoscaling for "${selectedNodegroupForEdit?.name ?? ""}"`,
      confirmButtonLabel: "Save",
      isLocked: isNodegroupUpdating,
      fields: [
        {
          name: "is_autoscaling_enabled",
          label: "Enable Autoscaling",
          type: FIELD_TYPES.TOGGLE,
          defaultValue: selectedNodegroupForEdit?.max_node_count != null
        },
        {
          name: "min_node_count",
          label: "Min Nodes",
          type: FIELD_TYPES.NUMBER,
          defaultValue:
            selectedNodegroupForEdit?.min_node_count != null
              ? selectedNodegroupForEdit.min_node_count.toString()
              : "1",
          min: 0,
          rules: number()
            .integer()
            .min(0)
            .when("is_autoscaling_enabled", {
              is: true,
              then: (s) => s.required()
            }),
          isHidden: (data) =>
            !(data as unknown as AutoscaleNodegroupFormData)
              .is_autoscaling_enabled
        },
        {
          name: "max_node_count",
          label: "Max Nodes",
          type: FIELD_TYPES.NUMBER,
          defaultValue:
            selectedNodegroupForEdit?.max_node_count != null
              ? selectedNodegroupForEdit.max_node_count.toString()
              : "1",
          min: 0,
          rules: number()
            .integer()
            .min(0)
            .when("is_autoscaling_enabled", {
              is: true,
              then: (s) =>
                s
                  .required()
                  .test("min-max", "Must be >= min nodes", function (v) {
                    const minCount = (
                      this.parent as unknown as AutoscaleNodegroupFormData
                    ).min_node_count;
                    return (
                      v === undefined || minCount === undefined || v >= minCount
                    );
                  })
            }),
          isHidden: (data) =>
            !(data as unknown as AutoscaleNodegroupFormData)
              .is_autoscaling_enabled
        }
      ]
    },
    [DIALOG_TYPES.NODEGROUP_UPGRADE]: {
      onConfirm: handleConfirmUpgradeNodegroup,
      title: `Upgrade Node Pool "${selectedNodegroupForEdit?.name ?? ""}"`,
      confirmButtonLabel: "Upgrade",
      isLocked: isClusterUpgrading || isNodegroupUpdating,
      fields: [
        {
          name: "cluster_template",
          type: FIELD_TYPES.SELECT,
          label: "Cluster template",
          helperText:
            "✍ You can choose the current version to re-apply the configuration.",
          options: () => {
            const currentTemplateName =
              selectedNodegroupForEdit?.labels?.kube_tag;
            const nodegroupImagePrefix = selectedNodegroupForEdit?.image_id
              ? selectedNodegroupForEdit.image_id.substring(0, 6).toLowerCase()
              : null;

            if (
              !currentTemplateName ||
              !clusterTemplates ||
              !nodegroupImagePrefix
            ) {
              return null;
            }

            const imageCompatibleTemplates = clusterTemplates.filter(
              (template) => {
                const templateImagePrefix = template.image_id
                  ? template.image_id.substring(0, 6).toLowerCase()
                  : null;
                return templateImagePrefix === nodegroupImagePrefix;
              }
            );

            return filterVersions(
              currentTemplateName,
              imageCompatibleTemplates,
              (obj) => obj.name
            ).map(({ name, uuid }) => ({
              label: name === currentTemplateName ? `${name} (current)` : name,
              value: uuid
            }));
          },
          rules: selectOptionSchema.required("Cluster template is required")
        }
      ]
    }
  };

  const currentDialogProps = dialog.isOpened
    ? dialogProps[dialog.type]
    : undefined;

  const componentTitle = cluster?.name || "Cluster Details";

  return (
    <>
      <Head title={componentTitle} />
      {organization && project && <Breadcrumbs breadcrumbs={breadcrumbs} />}

      <s.SummaryContainer>
        <s.SummaryColumn>
          <s.Title variant={"h4"} component={"h2"}>
            {componentTitle}{" "}
            {cluster?.status?.includes("DELETE") && "(Deleting...)"}
          </s.Title>
          {cluster && (
            <s.SummaryRow>
              <s.DetailsTitle>ID:</s.DetailsTitle>
              <s.DetailsInfoColored>{cluster.id}</s.DetailsInfoColored>
            </s.SummaryRow>
          )}
          {/* Add conditional Alert for cluster unhealthy state */}
          {cluster?.health_status && cluster.health_status !== "HEALTHY" && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Cluster health issue detected.
              {typeof cluster.health_status_reason === "string" &&
                cluster.health_status_reason !== "None" && (
                  <> Reason: {cluster.health_status_reason}</>
                )}
            </Alert>
          )}
          {/* Add conditional Alert for cluster faults */}
          {cluster?.faults &&
            typeof cluster.faults === "object" &&
            Object.keys(cluster.faults).length > 0 && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  Cluster Faults Detected:
                </Typography>
                <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                  {Object.entries(cluster.faults).map(([key, value]) => (
                    <li key={key}>
                      <Typography variant="body2">
                        <strong>{key}:</strong> {String(value)}
                        {/* Ensure value is string */}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
        </s.SummaryColumn>
        <s.ActionsContainer>
          <Tooltip title={"Delete Cluster"} placement={"top"} arrow>
            <span>
              <IconButton
                onClick={handleDeleteClusterButtonClick}
                disabled={isAnyOperationInProgress}
                color={"inherit"}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={"More actions"} placement={"top"} arrow>
            <span>
              <IconButton
                onClick={handleActionsMenuButtonClick}
                disabled={isAnyOperationInProgress}
                color={"inherit"}
              >
                <MoreVertIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            isOpened={isActionsMenuOpened}
            onClose={handleActionsMenuClose}
            anchorEl={actionsMenuButtonRef.current}
            items={clusterActions.map((action) => ({
              label: action.label,
              onClick: handleActionsMenuItemClick(action.handler),
              disabled: action.disabled
            }))}
          />
        </s.ActionsContainer>
      </s.SummaryContainer>

      {cluster && (
        <s.InfoContainer variant="outlined">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ flexBasis: "25%", minWidth: "200px" }}>
              <s.SectionTitle>General</s.SectionTitle>
              <s.InfoItem>
                <s.InfoLabel>Status:</s.InfoLabel>
                <s.Tag label={cluster.status} />
              </s.InfoItem>
              <s.InfoItem>
                <s.InfoLabel>Health:</s.InfoLabel>
                <s.Tag label={cluster.health_status || "N/A"} />
              </s.InfoItem>
              <s.InfoItem>
                <s.InfoLabel>Kube Version:</s.InfoLabel>
                <s.InfoValue>
                  {cluster.coe_version || cluster.labels.kube_tag || "N/A"}
                </s.InfoValue>
              </s.InfoItem>
              <s.InfoItem>
                <s.InfoLabel>Created:</s.InfoLabel>
                <s.InfoValue>
                  {cluster.created_at
                    ? formatDate(
                        new Date(cluster.created_at),
                        DATE_FORMATS.DATETIME
                      )
                    : "N/A"}
                </s.InfoValue>
              </s.InfoItem>
            </Box>

            <Box sx={{ flexBasis: "25%", minWidth: "200px" }}>
              <s.SectionTitle>Master Nodes</s.SectionTitle>
              <s.InfoItem>
                <s.InfoLabel>Count:</s.InfoLabel>
                <s.InfoValue>{cluster.master_count ?? "N/A"}</s.InfoValue>
              </s.InfoItem>
              <s.InfoItem>
                <s.InfoLabel>Flavor:</s.InfoLabel>
                <s.InfoValue>
                  {!flavors || flavors.length === 0
                    ? "Loading..."
                    : flavors.find((f) => f.id === cluster.master_flavor_id)
                        ?.name ||
                      cluster.master_flavor_id ||
                      "N/A"}
                </s.InfoValue>
              </s.InfoItem>
              <s.InfoItem>
                <s.InfoLabel>Updated:</s.InfoLabel>
                <s.InfoValue>
                  {cluster.updated_at
                    ? formatDate(
                        new Date(cluster.updated_at),
                        DATE_FORMATS.DATETIME
                      )
                    : "N/A"}
                </s.InfoValue>
              </s.InfoItem>
            </Box>

            <Box sx={{ flexBasis: "45%", minWidth: "250px" }}>
              <s.SectionTitle>Connection</s.SectionTitle>
              <s.InfoItem>
                <s.InfoLabel>API Address:</s.InfoLabel>
                <s.InfoValue>{cluster.api_address || "N/A"}</s.InfoValue>
              </s.InfoItem>
            </Box>
          </Box>
        </s.InfoContainer>
      )}

      <Tabs
        value={activeTabIndex}
        onChange={handleChangeTab}
        variant="scrollable"
        scrollButtons="auto"
      >
        {Object.keys(TAB_TITLES).map((keyStr) => {
          const key = Number(keyStr);
          return TAB_TITLES[key] ? (
            <Tab
              key={key}
              label={TAB_TITLES[key]}
              value={key}
              disabled={isAnyOperationInProgress}
            />
          ) : null;
        })}
      </Tabs>

      {activeTabIndex === TABS.NODE_POOLS && (
        <CommonTable<TableNodegroup>
          toolbarItems={nodePoolToolbar}
          columns={columns}
          rows={processedNodegroups || []}
          actions={nodegroupTableActions}
          isLoading={
            (!nodegroups && areNodegroupsLoading) ||
            (!flavors && areFlavorsLoading) ||
            !flavors ||
            !nodegroups
          }
          isSearchEnabled={true}
          isPaginationEnabled={false}
        />
      )}

      {activeTabIndex === TABS.SETTINGS && (
        <s.TabContentContainer variant="outlined">
          {areClusterSettingsLoading && !clusterSettings ? (
            <Typography variant="body2" color="text.secondary">
              Loading settings...
            </Typography>
          ) : groupedSettings.length > 0 ? (
            <>
              {groupedSettings.map(({ group, settings }) => (
                <s.InfoContainer key={group} variant="outlined">
                  <s.SectionTitle>{group}</s.SectionTitle>
                  {settings.map((setting) => (
                    <s.InfoItem key={setting.key}>
                      {setting.value_type === "bool" ? (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settingsValues[setting.key] === "true"}
                              onChange={() => handleSettingToggle(setting.key)}
                              disabled={
                                isAnyOperationInProgress ||
                                isClusterSettingsUpdating
                              }
                            />
                          }
                          label={setting.description}
                        />
                      ) : setting.value_type === "int" ? (
                        <>
                          <s.InfoLabel>{setting.description}</s.InfoLabel>
                          <TextField
                            type="number"
                            size="small"
                            value={settingsValues[setting.key] ?? ""}
                            onChange={(e) =>
                              setSettingsValues((prev) => ({
                                ...prev,
                                [setting.key]: e.target.value
                              }))
                            }
                            disabled={
                              isAnyOperationInProgress ||
                              isClusterSettingsUpdating
                            }
                            slotProps={{
                              htmlInput: {
                                min: setting.min,
                                max: setting.max
                              }
                            }}
                            sx={{ minWidth: 160 }}
                          />
                        </>
                      ) : (
                        <>
                          <s.InfoLabel>{setting.description}</s.InfoLabel>
                          <TextField
                            select={
                              setting.value_type === "list" &&
                              !!setting.allowed_values
                            }
                            size="small"
                            value={settingsValues[setting.key] ?? ""}
                            onChange={(e) =>
                              setSettingsValues((prev) => ({
                                ...prev,
                                [setting.key]: e.target.value
                              }))
                            }
                            disabled={
                              isAnyOperationInProgress ||
                              isClusterSettingsUpdating
                            }
                            sx={{ minWidth: 160 }}
                          >
                            {setting.value_type === "list" &&
                              setting.allowed_values?.map((v) => (
                                <MenuItem key={v} value={v}>
                                  {v}
                                </MenuItem>
                              ))}
                          </TextField>
                        </>
                      )}
                    </s.InfoItem>
                  ))}
                </s.InfoContainer>
              ))}
              {hasSettingsChanges && (
                <s.ActionsContainer>
                  <Button
                    variant="contained"
                    onClick={handleSaveSettings}
                    disabled={isClusterSettingsUpdating}
                  >
                    {isClusterSettingsUpdating ? "Saving..." : "Save"}
                  </Button>
                </s.ActionsContainer>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No configurable settings available for this cluster.
            </Typography>
          )}
        </s.TabContentContainer>
      )}

      {activeTabIndex === TABS.LABELS && (
        <CommonTable<LabelRow>
          columns={labelTableColumns}
          rows={labelRows}
          isLoading={!cluster}
          isSelectingEnabled={false}
          isSortingEnabled={false}
          isPaginationEnabled={false}
          isSearchEnabled={true}
        />
      )}

      {dialog.isOpened && currentDialogProps && (
        <FormDialog
          isOpened={dialog.isOpened}
          onCancel={handleCloseDialog}
          {...currentDialogProps}
        />
      )}
    </>
  );
};
